import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Flame, Award, Mountain, Sparkles, BookOpen, ShieldAlert, AlertTriangle, Clock, Compass, ArrowRight, Lock } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { usePlanState } from "@/hooks/usePlanState";
import { UpgradePrompt } from "@/components/LockedOverlay";

// Full badge catalog with stories — used by Adventurer's Journal sheet and pass cards.
// Order matters: this is the display order.
const BADGE_CATALOG = [
  { id: "first-steps",      icon: "👣", name: "First Steps",      desc: "Awarded when a climber joins the expedition. Every adventure has a first step." },
  { id: "trail-blazer",     icon: "🥾", name: "Trail Blazer",     desc: "Completed Base Camp on any expedition. The ascent begins here." },
  { id: "summit-conqueror", icon: "🏔️", name: "Summit Conqueror", desc: "Finished a full expedition all the way to the Summit." },
  { id: "high-altitude",    icon: "☁️", name: "High Altitude",    desc: "Earned 500 Altitude Points across all expeditions." },
  { id: "compass-keeper",   icon: "🧭", name: "Compass Keeper",   desc: "Climbed 7 days in a row. Consistency keeps the trail open." },
];

const BADGE_BY_ID = Object.fromEntries(BADGE_CATALOG.map((b) => [b.id, b]));

const passNumber = (id) => (id || "00000000").replace(/-/g, "").slice(0, 8).toUpperCase();

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [dash, setDash] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [welcomeStep, setWelcomeStep] = useState("intro"); // "intro" | "form"
  const [newStudent, setNewStudent] = useState({ name: "", grade: "5" });
  const [loading, setLoading] = useState(true);
  const [journalClimber, setJournalClimber] = useState(null);
  const [recentQuizzes, setRecentQuizzes] = useState([]);
  const { state: planState, usageForClimber } = usePlanState();

  const loadStudents = async () => {
    const r = await api.get("/students");
    setStudents(r.data);
    if (r.data.length && !activeId) setActiveId(r.data[0].id);
    if (!r.data.length) {
      setLoading(false);
      // First-time experience: open welcome modal if account has zero climbers
      // and the user hasn't dismissed it this session yet.
      const dismissed = sessionStorage.getItem("mm_welcome_dismissed") === "1";
      if (!dismissed) {
        setWelcomeStep("intro");
        setShowWelcome(true);
      }
    }
    return r.data;
  };

  useEffect(() => { loadStudents(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (!activeId) return;
    setLoading(true);
    api.get(`/dashboard/student/${activeId}`).then((r) => setDash(r.data)).finally(() => setLoading(false));
    // Load recent quiz history for this climber — used by the "Recent quizzes" summary.
    api.get(`/students/${activeId}/quiz-history`)
      .then((r) => setRecentQuizzes((r.data?.quizzes || []).slice(0, 5)))
      .catch(() => setRecentQuizzes([]));
  }, [activeId]);

  const addStudent = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/students", newStudent);
      toast.success(`${r.data.name} joined the expedition!`);
      setShowAdd(false);
      setShowWelcome(false);
      sessionStorage.setItem("mm_welcome_dismissed", "1");
      setNewStudent({ name: "", grade: "5" });
      await loadStudents();
      setActiveId(r.data.id);
      // Send the new climber straight into the personalization quiz.
      navigate(`/climbers/${r.data.id}/onboarding`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add climber");
    }
  };

  const dismissWelcome = () => {
    setShowWelcome(false);
    sessionStorage.setItem("mm_welcome_dismissed", "1");
  };

  const toggleLeaderboard = async () => {
    if (!dash) return;
    const r = await api.patch(`/students/${dash.student.id}/leaderboard`);
    toast.success(`Leaderboard ${r.data.leaderboard_enabled ? "enabled" : "disabled"}`);
    setDash({ ...dash, student: { ...dash.student, leaderboard_enabled: r.data.leaderboard_enabled } });
  };

  // Memoize flagged climbers so we don't filter on every render.
  const flaggedStudents = useMemo(
    () => students.filter((s) => (s.flag_count || 0) > 0),
    [students]
  );

  return (
    <div className="min-h-screen bg-mm-parchment bg-topo">
      <Navbar />

      <div className="container mx-auto py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] font-bold text-mm-steel">Expedition HQ</div>
            <h1 className="font-heading text-4xl sm:text-5xl font-black text-mm-navy mt-2" data-testid="dashboard-welcome">
              Welcome back, {user?.name?.split(" ")[0] || "Climber"}.
            </h1>
            <p className="text-mm-navy/70 mt-2">Manage your climbers, generate expeditions, track every summit.</p>
          </div>
          <div className="flex items-center gap-3">
            {user?.effective_plan !== "premium" && (
              <Link to="/pricing" className="btn-trail btn-outline-navy text-sm" data-testid="dashboard-upgrade-btn">Upgrade</Link>
            )}
            {user?.effective_plan === "premium" && user?.stripe_customer_id !== undefined && !user?.is_admin && (
              <button
                onClick={async () => {
                  try {
                    const r = await api.post("/payments/portal", { origin_url: window.location.origin });
                    window.location.href = r.data.url;
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Could not open billing portal");
                  }
                }}
                className="btn-trail btn-outline-navy text-sm"
                data-testid="dashboard-manage-sub-btn"
              >
                Manage subscription
              </button>
            )}
            <button className="btn-trail btn-gold text-sm" onClick={() => setShowAdd(true)} data-testid="dashboard-add-student-btn">
              <Plus size={16} strokeWidth={2.5} /> Add climber
            </button>
          </div>
        </div>

        {/* Trial status banner */}
        {planState && !planState.is_premium && planState.trial_expired && (
          <div className="mt-6 bg-amber-50 border-2 border-amber-300 rounded-2xl p-5 flex items-start gap-4" data-testid="trial-expired-banner">
            <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-800 flex items-center justify-center shrink-0">
              <AlertTriangle size={20} strokeWidth={2.5} />
            </div>
            <div className="flex-1">
              <div className="font-heading font-bold text-amber-900">Your Summit Pass trial has ended</div>
              <p className="text-sm text-amber-900/85 mt-1">
                You are now on the Free Climb — finish your current expedition then upgrade to keep all your trails open! 🧭
              </p>
              {planState.grace?.in_grace && (
                <p className="text-xs text-amber-900/75 mt-2" data-testid="grace-countdown">
                  <Clock size={12} strokeWidth={2.5} className="inline mr-1" />
                  {planState.grace.days_remaining === 1
                    ? "1 day of in-progress expedition access remaining"
                    : `${planState.grace.days_remaining} days of in-progress expedition access remaining`}
                </p>
              )}
            </div>
            <Link to="/pricing" className="btn-trail btn-gold text-sm shrink-0" data-testid="trial-expired-cta">
              Upgrade Now
            </Link>
          </div>
        )}
        {!user?.trial_expired && user?.is_trial_active && (user?.trial_days_remaining ?? 99) <= 3 && (
          <div className="mt-6 bg-mm-gold/15 border-2 border-mm-gold rounded-2xl p-5 flex items-start gap-4" data-testid="trial-ending-banner">
            <div className="w-10 h-10 rounded-xl bg-mm-gold/30 text-mm-navy flex items-center justify-center shrink-0">
              <Clock size={20} strokeWidth={2.5} />
            </div>
            <div className="flex-1">
              <div className="font-heading font-bold text-mm-navy">
                {user.trial_days_remaining === 1
                  ? "Your trial ends tomorrow"
                  : `Your trial ends in ${user.trial_days_remaining} days`}
              </div>
              <p className="text-sm text-mm-navy/75 mt-1">
                Add a payment method now to keep Premium access — unlimited expeditions, Rescue Route, and Explorer's Challenge.
              </p>
            </div>
            <Link to="/pricing" className="btn-trail btn-gold text-sm shrink-0" data-testid="trial-ending-cta">Add payment</Link>
          </div>
        )}

        {/* Persistent Free-Climb info card — shown for all non-premium users */}
        {planState && !planState.is_premium && (
          <div className="mt-6 stage-card p-5 flex items-start gap-4" data-testid="free-climb-card">
            <div className="w-10 h-10 rounded-xl bg-mm-gold/25 text-mm-navy flex items-center justify-center shrink-0">
              <Mountain size={20} strokeWidth={2.5} />
            </div>
            <div className="flex-1">
              <div className="font-heading font-bold text-mm-navy">You are on the Free Climb</div>
              <p className="text-sm text-mm-navy/75 mt-1">
                Upgrade to Summit Pass for $5/month to unlock unlimited expeditions, all activity types, personalized learning paths, and the full badge collection.
              </p>
              {/* Per-climber expedition usage progress bar(s) */}
              {(planState.climbers || []).length > 0 && (
                <div className="mt-3 space-y-2" data-testid="free-climb-usage">
                  {planState.climbers.map((c) => {
                    const used = c.free_full_expedition_used ? 1 : 0;
                    return (
                      <div key={c.climber_id} className="flex items-center gap-3" data-testid={`free-climb-usage-${c.climber_id}`}>
                        <div className="text-xs text-mm-navy/70 min-w-[100px]">{c.climber_name}</div>
                        <div className="flex-1 h-2 rounded-full bg-mm-navy/10 overflow-hidden">
                          <div className={`h-full ${used >= 1 ? "bg-mm-gold" : "bg-mm-steel"}`} style={{ width: `${used * 100}%` }} />
                        </div>
                        <div className="text-[11px] text-mm-navy/60 font-bold whitespace-nowrap">
                          {used} of 1 free expedition{used === 1 ? "s" : ""} used
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <Link to="/pricing" className="btn-trail btn-gold text-sm shrink-0" data-testid="free-climb-upgrade-btn">
              Upgrade Now
            </Link>
          </div>
        )}

        {/* Parent notification — flagged inputs */}
        {flaggedStudents.length > 0 && (
          <div className="mt-6 bg-amber-50 border-2 border-amber-300 rounded-2xl p-5 flex items-start gap-4" data-testid="parent-safety-banner">
            <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
              <ShieldAlert size={20} strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-heading font-bold text-amber-900">Some inputs were flagged as inappropriate</div>
              <p className="text-sm text-amber-900/80 mt-1">
                One or more climbers had inputs blocked by our content safety filter. Talk to your climber about staying on the trail — and remember, all expeditions stay age-appropriate.
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {flaggedStudents.map((s) => (
                  <span key={s.id} className="px-3 py-1 rounded-full bg-amber-100 text-amber-900 text-xs font-bold" data-testid={`flag-badge-${s.id}`}>
                    {s.name}: {s.flag_count} flag{s.flag_count === 1 ? "" : "s"}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Students */}
        <div className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-bold text-mm-navy">Your climbers</h2>
          </div>
          <div className="flex flex-wrap gap-3">
            {students.map((s) => (
              <button
                key={s.id}
                onClick={() => setActiveId(s.id)}
                className={`px-5 py-3 rounded-2xl border-2 text-left transition-all ${
                  activeId === s.id
                    ? "bg-mm-navy text-white border-mm-navy"
                    : "bg-white border-mm-navy/15 text-mm-navy hover:border-mm-steel"
                }`}
                data-testid={`student-tab-${s.id}`}
              >
                <div className="font-heading font-bold">{s.name}</div>
                <div className="text-xs opacity-80">Grade {s.grade} • {s.altitude_points} AP</div>
              </button>
            ))}
            {!students.length && (
              <div className="text-mm-navy/60">No climbers yet — add your first one to start the expedition.</div>
            )}
          </div>
        </div>

        {/* Welcome / Add-climber modal — single fixed-size shell so the container does NOT resize when content swaps */}
        {(showWelcome || showAdd) && (
          <div
            className="fixed inset-0 bg-mm-navy/40 backdrop-blur-sm z-50 flex items-center justify-center px-4"
            data-testid="welcome-or-add-modal"
            role="dialog"
            aria-modal="true"
          >
            <div
              className="bg-white rounded-2xl w-full max-w-md min-h-[420px] overflow-hidden relative shadow-2xl border border-mm-navy/10"
              style={{ height: 460 }}
            >
              {/* Welcome intro pane */}
              <div
                className={`absolute inset-0 p-8 flex flex-col transition-all duration-500 ${
                  showWelcome && welcomeStep === "intro" && !showAdd
                    ? "opacity-100 translate-x-0"
                    : "opacity-0 -translate-x-6 pointer-events-none"
                }`}
                aria-hidden={!(showWelcome && welcomeStep === "intro" && !showAdd)}
                data-testid="welcome-intro-pane"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-mm-gold/25 text-mm-navy flex items-center justify-center">
                    <Compass size={22} strokeWidth={2.5} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.22em] font-bold text-mm-steel">Welcome to Mind Map</div>
                    <h3 className="font-heading text-2xl font-black text-mm-navy">Plot your first path</h3>
                  </div>
                </div>
                <p className="text-mm-navy/75 mt-5 leading-relaxed text-[15px]">
                  Mind Map turns any topic into an adventure. Add your first <strong>climber</strong> — a child, a student, or yourself — and we'll build their personalized expedition: Base Camp, three trails, and a Summit challenge.
                </p>
                <ul className="mt-4 space-y-2 text-sm text-mm-navy/80">
                  <li className="flex items-start gap-2"><span className="text-mm-gold mt-0.5">●</span><span>Activities tuned to grade level</span></li>
                  <li className="flex items-start gap-2"><span className="text-mm-gold mt-0.5">●</span><span>Altitude Points, badges, daily streak</span></li>
                  <li className="flex items-start gap-2"><span className="text-mm-gold mt-0.5">●</span><span>Free 14-day Summit Pass trial</span></li>
                </ul>
                <div className="mt-auto pt-6 flex items-center justify-between gap-3">
                  <button
                    type="button"
                    onClick={dismissWelcome}
                    className="text-mm-navy/60 hover:text-mm-navy text-sm font-bold"
                    data-testid="welcome-skip-btn"
                  >
                    Maybe later
                  </button>
                  <button
                    type="button"
                    onClick={() => setWelcomeStep("form")}
                    className="btn-trail btn-gold text-sm"
                    data-testid="welcome-add-climber-btn"
                  >
                    Add a climber <ArrowRight size={14} strokeWidth={2.5} />
                  </button>
                </div>
              </div>

              {/* Add-climber form pane (used by both the welcome step-2 and the top-right Add button) */}
              <form
                onSubmit={addStudent}
                className={`absolute inset-0 p-8 flex flex-col transition-all duration-500 ${
                  showAdd || (showWelcome && welcomeStep === "form")
                    ? "opacity-100 translate-x-0"
                    : "opacity-0 translate-x-6 pointer-events-none"
                }`}
                aria-hidden={!(showAdd || (showWelcome && welcomeStep === "form"))}
                data-testid="add-student-modal"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-mm-steel/20 text-mm-navy flex items-center justify-center">
                    <Plus size={22} strokeWidth={2.5} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.22em] font-bold text-mm-steel">Trail roster</div>
                    <h3 className="font-heading text-2xl font-black text-mm-navy">Add a climber</h3>
                  </div>
                </div>
                <p className="text-mm-navy/70 mt-3 text-sm">
                  Their grade tunes the difficulty and reading level of every expedition.
                </p>
                <div className="mt-5 space-y-3">
                  <input
                    placeholder="Climber name"
                    value={newStudent.name}
                    onChange={(e) => setNewStudent({ ...newStudent, name: e.target.value })}
                    required
                    className="w-full border-2 border-mm-navy/15 rounded-lg px-4 py-3 focus:outline-none focus:border-mm-steel focus:ring-2 focus:ring-mm-steel/30"
                    data-testid="add-student-name-input"
                  />
                  <select
                    value={newStudent.grade}
                    onChange={(e) => setNewStudent({ ...newStudent, grade: e.target.value })}
                    className="w-full border-2 border-mm-navy/15 rounded-lg px-4 py-3 bg-white focus:outline-none focus:border-mm-steel"
                    data-testid="add-student-grade-select"
                  >
                    <option value="K">Kindergarten</option>
                    {[...Array(12)].map((_, i) => <option key={i + 1} value={String(i + 1)}>Grade {i + 1}</option>)}
                  </select>
                </div>
                <div className="mt-auto pt-6 flex gap-3 justify-between items-center">
                  <button
                    type="button"
                    onClick={() => {
                      if (showWelcome) {
                        // back to intro within the welcome flow
                        setWelcomeStep("intro");
                      } else {
                        setShowAdd(false);
                      }
                    }}
                    className="text-mm-navy/60 hover:text-mm-navy text-sm font-bold"
                    data-testid="add-student-back-btn"
                  >
                    {showWelcome ? "Back" : "Cancel"}
                  </button>
                  <button type="submit" className="btn-trail btn-gold text-sm" data-testid="add-student-submit-btn">
                    Add to expedition
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Adventurer's Journal + Active expeditions */}
        {dash && (
          <>
            <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Active expeditions — left column (2/3) */}
              <div className="lg:col-span-2 stage-card p-7">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading text-xl font-bold text-mm-navy">Active expeditions</h3>
                  <button
                    onClick={() => navigate(`/expeditions/new?studentId=${dash.student.id}`)}
                    className="btn-trail btn-gold text-sm"
                    data-testid="new-expedition-btn"
                  >
                    <Sparkles size={16} strokeWidth={2.5} /> Generate expedition
                  </button>
                </div>

                <div className="mt-5 space-y-3">
                  {dash.active_expeditions.length === 0 && (
                    <div className="text-mm-navy/60 py-6 text-center border-2 border-dashed border-mm-navy/15 rounded-xl">
                      No active expeditions. Generate one to start climbing.
                    </div>
                  )}
                  {dash.active_expeditions.map((e) => {
                    const pct = Math.round((e.completed_stages.length / e.stages.length) * 100);
                    return (
                      <Link key={e.id} to={`/expeditions/${e.id}`} className="block p-5 rounded-xl border border-mm-navy/10 bg-mm-parchment hover:border-mm-steel transition-colors" data-testid={`active-exp-${e.id}`}>
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <div className="font-heading font-bold text-mm-navy">{e.title}</div>
                            <div className="text-xs text-mm-navy/60">{e.topic} • {e.difficulty === "rescue" ? "Rescue Route" : e.difficulty === "explorer" ? "Explorer's Challenge" : "Standard Trail"}</div>
                          </div>
                          <div className="text-right text-xs text-mm-navy/70 font-bold">{pct}%</div>
                        </div>
                        <div className="elevation-bar mt-3">
                          <div className="elevation-fill" style={{ width: `${pct}%` }} />
                        </div>
                      </Link>
                    );
                  })}
                </div>

                {/* Leaderboard toggle (kept here so it's not lost with the badges column) */}
                <div className="mt-6 pt-6 border-t border-mm-navy/10">
                  {planState && !planState.is_premium ? (
                    <div className="flex items-center justify-between gap-3" data-testid="leaderboard-locked">
                      <div className="flex items-center gap-2">
                        <Lock size={14} className="text-mm-gold" strokeWidth={2.5} />
                        <div>
                          <div className="text-sm font-bold text-mm-navy">Leaderboard for {dash.student.name}</div>
                          <p className="text-xs text-mm-navy/60 mt-0.5">Summit Pass unlocks leaderboard access for your family.</p>
                        </div>
                      </div>
                      <Link to="/pricing" className="text-xs font-bold text-mm-navy underline hover:text-mm-steel" data-testid="leaderboard-upgrade-link">
                        Upgrade Now →
                      </Link>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-bold text-mm-navy">Leaderboard for {dash.student.name}</div>
                        <p className="text-xs text-mm-navy/60 mt-0.5">Parents can opt-in to share progress between climbers.</p>
                      </div>
                      <button
                        onClick={toggleLeaderboard}
                        className={`px-3 py-1 rounded-full text-xs font-bold ${dash.student.leaderboard_enabled ? "bg-mm-gold text-mm-navy" : "bg-mm-navy/10 text-mm-navy"}`}
                        data-testid="toggle-leaderboard-btn"
                      >
                        {dash.student.leaderboard_enabled ? "On" : "Off"}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Adventurer's Journal — right column (1/3) */}
              <div className="stage-card p-7" data-testid="adventurers-journal">
                <div className="flex items-center gap-2">
                  <BookOpen size={18} className="text-mm-steel" strokeWidth={2.5} />
                  <h3 className="font-heading text-xl font-bold text-mm-navy">Adventurer's Journal</h3>
                </div>
                <p className="text-xs text-mm-navy/60 mt-1">Tap a climber's pass to see their badges.</p>

                <div className="mt-5 space-y-4">
                  {students.length === 0 && (
                    <div className="text-mm-navy/60 text-sm py-6 text-center border-2 border-dashed border-mm-navy/15 rounded-xl">
                      No climbers in the journal yet.
                    </div>
                  )}
                  {students.map((s) => (
                    <AdventurerPass
                      key={s.id}
                      climber={s}
                      onOpen={() => setJournalClimber(s)}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Recent quiz history — quick parent-facing summary with a link to full report */}
            {dash && (
              <div className="mt-8 stage-card p-7" data-testid="recent-quizzes-card">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <h3 className="font-heading text-xl font-bold text-mm-navy flex items-center gap-2">
                    <BookOpen size={18} strokeWidth={2.5} className="text-mm-steel" />
                    Recent quizzes — {dash.student.name}
                  </h3>
                  <Link
                    to={`/climbers/${dash.student.id}/quiz-history`}
                    className="text-sm font-bold text-mm-steel hover:text-mm-navy flex items-center gap-1"
                    data-testid="view-full-quiz-history-link"
                  >
                    View full history <ArrowRight size={14} strokeWidth={2.5} />
                  </Link>
                </div>
                <div className="mt-4 space-y-2">
                  {recentQuizzes.length === 0 && (
                    <div className="text-mm-navy/60 text-sm py-4 text-center border-2 border-dashed border-mm-navy/15 rounded-xl">
                      No quizzes completed yet. Trail checks and Summit quizzes will appear here.
                    </div>
                  )}
                  {recentQuizzes.map((q, i) => {
                    const percent = Math.round(q.score_percent || 0);
                    const correctCount = (q.results || []).filter((r) => r.is_correct).length;
                    const total = (q.results || []).length;
                    const scoreColor = percent >= 70 ? "text-emerald-700" : percent >= 50 ? "text-amber-700" : "text-red-700";
                    const stageLabel = ({ base_camp: "Base Camp", trail_1: "Trail 1", trail_2: "Trail 2", trail_3: "Trail 3", summit: "Summit" })[q.stage_name] || q.stage_name;
                    return (
                      <Link
                        key={`${q.expedition_id}-${q.stage_name}-${i}`}
                        to={`/climbers/${dash.student.id}/quiz-history`}
                        className="flex items-center justify-between gap-3 p-3 rounded-lg border border-mm-navy/10 bg-mm-parchment hover:border-mm-steel transition-colors"
                        data-testid={`recent-quiz-${i}`}
                      >
                        <div className="min-w-0">
                          <div className="font-heading font-bold text-mm-navy truncate">{stageLabel} · {q.expedition_topic}</div>
                          <div className="text-[11px] text-mm-navy/60">{correctCount}/{total} correct · +{q.ap_earned || 0} AP</div>
                        </div>
                        <div className={`font-heading text-lg font-black shrink-0 ${scoreColor}`}>{percent}%</div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Completed history */}
            {dash.completed_expeditions.length > 0 && (
              <div className="mt-8 stage-card p-7">
                <h3 className="font-heading text-xl font-bold text-mm-navy flex items-center gap-2"><BookOpen size={18} strokeWidth={2.5} className="text-mm-steel" /> Completed expeditions</h3>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                  {dash.completed_expeditions.map((e) => {
                    const scores = Object.values(e.stage_scores || {});
                    const avg = scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0;
                    return (
                      <div key={e.id} className="p-4 rounded-xl border border-mm-navy/10 bg-mm-parchment" data-testid={`completed-exp-${e.id}`}>
                        <div className="font-heading font-bold text-mm-navy">{e.title}</div>
                        <div className="text-xs text-mm-navy/60 mt-0.5">{e.topic}</div>
                        <div className="mt-2 text-sm font-bold text-mm-gold">Mastery: {avg}%</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {/* Journal badge sheet — slides in from right */}
        <Sheet open={!!journalClimber} onOpenChange={(open) => !open && setJournalClimber(null)}>
          <SheetContent side="right" className="bg-mm-parchment border-l-2 border-mm-navy/10 w-full sm:max-w-md overflow-y-auto" data-testid="adventurer-sheet">
            {journalClimber && (
              <>
                <SheetHeader className="text-left">
                  <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-mm-steel">Adventurer's Journal</div>
                  <SheetTitle className="font-heading text-3xl font-black text-mm-navy">{journalClimber.name}</SheetTitle>
                  <SheetDescription className="text-mm-navy/70">
                    Grade {journalClimber.grade} · Pass #{passNumber(journalClimber.id)}
                  </SheetDescription>
                </SheetHeader>

                <div className="mt-5 grid grid-cols-3 gap-2">
                  <SheetStat icon={<Mountain size={14} strokeWidth={2.5} />} label="Altitude" value={journalClimber.altitude_points || 0} />
                  <SheetStat icon={<Flame size={14} strokeWidth={2.5} />} label="Streak" value={`${journalClimber.compass_streak || 0}d`} />
                  <SheetStat icon={<Award size={14} strokeWidth={2.5} />} label="Badges" value={(journalClimber.badges || []).length} />
                </div>

                <div className="mt-7">
                  <div className="text-xs uppercase tracking-widest font-bold text-mm-steel mb-3">Badge collection</div>
                  <div className="space-y-3" data-testid="adventurer-sheet-badges">
                    {BADGE_CATALOG.map((b) => {
                      const earned = (journalClimber.badges || []).includes(b.id);
                      return (
                        <div
                          key={b.id}
                          className={`flex items-start gap-3 p-3 rounded-xl border-2 transition-colors ${
                            earned
                              ? "bg-white border-mm-gold/40"
                              : "bg-white/60 border-mm-navy/10 opacity-60"
                          }`}
                          data-testid={`sheet-badge-${b.id}`}
                        >
                          <div className={`shrink-0 w-11 h-11 rounded-xl flex items-center justify-center text-xl ${earned ? "bg-mm-gold/20" : "bg-mm-navy/5"}`}>
                            {earned ? <span aria-hidden="true">{b.icon}</span> : <Lock size={16} strokeWidth={2.5} className="text-mm-navy/30" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-heading font-bold text-mm-navy text-sm flex items-center gap-2">
                              {b.name}
                              {earned && <span className="text-[9px] uppercase tracking-widest font-bold text-mm-gold">Earned</span>}
                            </div>
                            <div className="text-xs text-mm-navy/70 mt-0.5 leading-snug">{b.desc}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-7 flex gap-2">
                  <Link
                    to="/expeditions/new"
                    onClick={() => { setActiveId(journalClimber.id); setJournalClimber(null); }}
                    className="btn-trail btn-gold text-sm"
                    data-testid="sheet-new-expedition-btn"
                  >
                    <Sparkles size={14} strokeWidth={2.5} /> New expedition
                  </Link>
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>

        {!dash && students.length > 0 && loading && (
          <div className="mt-12 text-center text-mm-navy/60">Loading expedition data...</div>
        )}
      </div>
    </div>
  );
};

const AdventurerPass = ({ climber, onOpen }) => {
  const earned = climber.badges || [];
  const ap = climber.altitude_points || 0;
  const streak = climber.compass_streak || 0;
  // Show the most recent (last in array) earned badges, up to 4 icons.
  const recent = earned.slice(-4).map((id) => BADGE_BY_ID[id]).filter(Boolean);

  return (
    <button
      type="button"
      onClick={onOpen}
      className="group block w-full text-left rounded-2xl overflow-hidden bg-mm-parchment border-2 border-mm-navy hover:border-mm-gold transition-all shadow-sm hover:shadow-md"
      data-testid={`adventurer-pass-${climber.id}`}
      aria-label={`Open ${climber.name}'s journal`}
    >
      {/* Top stripe — gold ribbon */}
      <div className="bg-mm-navy text-white px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Compass size={12} strokeWidth={2.5} className="text-mm-gold" />
          <span className="text-[10px] uppercase tracking-[0.18em] font-bold">Adventurer's Pass</span>
        </div>
        <span className="text-[10px] font-mono text-mm-gold/80">#{passNumber(climber.id)}</span>
      </div>

      <div className="p-4 bg-topo">
        <div className="flex items-baseline justify-between gap-2">
          <div className="font-heading text-lg font-black text-mm-navy truncate">{climber.name}</div>
          <span className="text-[10px] uppercase tracking-widest font-bold text-mm-steel shrink-0">Grade {climber.grade}</span>
        </div>

        <div className="mt-3 grid grid-cols-3 gap-1.5">
          <PassStat icon={<Mountain size={11} strokeWidth={2.5} />} label="AP" value={ap} />
          <PassStat icon={<Flame size={11} strokeWidth={2.5} />} label="Streak" value={`${streak}d`} />
          <PassStat icon={<Award size={11} strokeWidth={2.5} />} label="Badges" value={earned.length} />
        </div>

        <div className="mt-3 pt-3 border-t border-dashed border-mm-navy/15 flex items-center justify-between">
          <div className="flex items-center gap-1" aria-label={`${earned.length} of ${BADGE_CATALOG.length} badges earned`}>
            {recent.length === 0 && (
              <span className="text-[10px] text-mm-navy/40 uppercase tracking-widest font-bold">No badges yet</span>
            )}
            {recent.map((b) => (
              <span key={b.id} title={b.name} className="w-6 h-6 rounded-full bg-mm-gold/20 text-base flex items-center justify-center" aria-hidden="true">
                {b.icon}
              </span>
            ))}
            {earned.length > 4 && (
              <span className="text-[10px] font-bold text-mm-navy/60 ml-1">+{earned.length - 4}</span>
            )}
          </div>
          <span className="text-[10px] uppercase tracking-widest font-bold text-mm-steel group-hover:text-mm-navy transition-colors flex items-center gap-1">
            View <ArrowRight size={10} strokeWidth={2.5} />
          </span>
        </div>
      </div>
    </button>
  );
};

const PassStat = ({ icon, label, value }) => (
  <div className="rounded-md bg-white/70 border border-mm-navy/10 px-1.5 py-1.5 text-center">
    <div className="text-mm-steel flex justify-center">{icon}</div>
    <div className="font-heading font-black text-mm-navy text-sm leading-none mt-0.5">{value}</div>
    <div className="text-[8px] uppercase tracking-widest text-mm-steel font-bold mt-0.5">{label}</div>
  </div>
);

const SheetStat = ({ icon, label, value }) => (
  <div className="rounded-xl bg-white border border-mm-navy/10 p-3 text-center">
    <div className="text-mm-steel flex justify-center">{icon}</div>
    <div className="font-heading font-black text-mm-navy text-lg mt-1">{value}</div>
    <div className="text-[10px] uppercase tracking-widest text-mm-steel font-bold mt-0.5">{label}</div>
  </div>
);

export default Dashboard;
